import { AnimationEvent } from "@angular/animations";
import { CommonModule } from "@angular/common";
import {
    AfterContentInit,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { scrollIntoView } from "seamless-scroll-polyfill";
import { v4 } from "uuid";

import { getExpandCollapseAnimation } from "@hermes/aphrodite/animations";
import { LayoutFacade } from "@hermes/aphrodite/layout";
import { Logger } from "@hermes/logger";
import { LOGGER } from "@hermes/web-logger";

import { iconAnimation } from "../animations/icon.animation";
import { ExpansionPanelPartDirective } from "../directives/expansion-panel-part.directive";

import {
    ARROW,
    ExpansionPanelIconComponent,
    ExpansionPanelIconStyle,
} from "./expansion-panel-icon/expansion-panel-icon.component";

export const DISCLOSURE = "disclosure";
export const ACCORDION = "accordion";
export const TREE = "tree";
export type ExpansionPanelAppearance =
    | typeof DISCLOSURE
    | typeof ACCORDION
    | typeof TREE;

export const SECONDARY_COLOR_LIGHT = "secondary-color-light";
export const SECONDARY_COLOR = "secondary-color";
export const TRANSPARENT_COLOR = "transparent-color";
export type BackgroundColor =
    | typeof SECONDARY_COLOR_LIGHT
    | typeof SECONDARY_COLOR
    | typeof TRANSPARENT_COLOR;

/**
 * This component implements a collapsable element (ExpansionPanel).
 *
 * It manages accessibility attributes for you.
 *PRIMARY
 * And takes its inputs from its parents.
 *
 * Usage:
 * ```
 * <h-expansion-panel [(collapsed)]="!isOpen">
 *      <ng-template hExpansionPanel="title">
 *          <ng-container i18n="@hermes.apollo.translation.key"></ng-container>
 *      </ng-template>
 *      <ng-template hExpansionPanel="content">
 *          <div class="my-expansion-panel-content">Content</div>
 *      </ng-template>
 * </h-expansion-panel>
 * ```
 *
 */
@Component({
    selector: "h-expansion-panel",
    templateUrl: "./expansion-panel.component.html",
    styleUrls: ["./expansion-panel.component.scss"],
    animations: [getExpandCollapseAnimation, iconAnimation],
    imports: [CommonModule, ExpansionPanelIconComponent],
    standalone: true,
})
export class ExpansionPanelComponent implements OnChanges, AfterContentInit {
    /**
     * For automated tests
     */
    @Input()
    public dataTestId?: string;

    /**
     * For aria describedby
     */
    @Input()
    public buttonDescription?: string;

    /**
     * True: Displays the arrow icon
     */
    @Input()
    public showIcon? = true;

    /**
     * Content container
     */
    @ViewChild("expansionPanelContent")
    public contentElement!: ElementRef<HTMLElement>;

    /**
     * Unique id for this expansion panel.
     * Can be overridden.
     * Id for reference between header and content.
     */
    @Input()
    public expansionPanelId: string = v4();

    /**
     * Collapsed flag, can be used with double-data binding to control the ExpansionPanel.
     */
    @Input()
    public collapsed = true;

    /**
     * Duration for the collapsed animation in
     */
    @Input()
    public animationDuration = 350;

    /**
     * Notify parent of collapsed value change.
     */
    @Output()
    public collapsedChange: EventEmitter<{
        id: string;
        value: boolean;
    }> = new EventEmitter<{ id: string; value: boolean }>();

    /**
     * Event emitted after the expand animation ended.
     */
    @Output()
    public expand: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Event emitted after the collapse animation ended.
     */
    @Output()
    public collapse: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Disable or enable the collapsable.
     */
    @Input()
    public collapsable = true;

    /**
     * Select content
     */
    @ContentChildren(ExpansionPanelPartDirective)
    public expansionPanelParts!: QueryList<unknown>;

    /**
     * Scroll to the clicked item
     */
    @Input()
    public adjustScroll = false;

    /**
     * Scroll Logical Position
     */
    @Input()
    public scrollPosition: ScrollLogicalPosition = "center";

    /**
     * Scroll Behavior
     */
    @Input()
    public scrollBehavior: ScrollBehavior = "instant";

    /**
     * Ajust the heading level for accessibility
     * Default value at 4 for backward compatibility
     */
    @Input()
    public headerLevel = 4;

    /**
     * Background color
     */
    @Input()
    public backgroundColor: BackgroundColor = SECONDARY_COLOR_LIGHT;

    @Input()
    public hasBlackFontColor = false;

    /**
     * true : remove border from expansion panel header
     */
    @Input()
    public noBorder = false;

    /**
     * true : remove bottom border from expansion panel header
     */
    @Input()
    public borderBottomRemoved = false;

    /**
     * true : add border on content when expanded
     */
    @Input()
    public borderBottomContent = false;

    /**
     * true : remove padding on header
     */
    @Input()
    public noPaddingHeader = false;

    /**
     * customize content padding
     */
    @Input()
    public contentPadding = "25px";

    @Input()
    public noOutlineOffset = false;

    /**
     * true : expansion panel have an icon
     */
    @Input()
    public hasExpansionPanelIcon = false;

    @Input()
    public role!: ExpansionPanelAppearance;

    @Input()
    public expansionPanelIconStyle: ExpansionPanelIconStyle = ARROW;

    @Input()
    public applyFocusOffset: boolean = true;

    @Input()
    public headerLeftPadding: number = 0;

    @Input()
    public isHeaderFontSizeLarge: boolean = false;

    @Input()
    public hasButtonSmallMinHeight = false;

    @Input()
    public marginTopHeader = 0;

    @Input()
    public shouldPlusMinusSvgHaveSmallDimensions = false;

    @Input()
    public shouldPlusMinusSvgHavePrimaryDarkStroke = false;

    @Input()
    public shouldPlusMinusSvgHaveSmallStrokeWidth = false;

    /**
     * Select components to render provided as children from outside.
     */
    public contentRef!: TemplateRef<unknown>;

    /**
     * the component should provide us with an already translated title.
     * this allow the expansion panel to not be aware of every translatiosn
     */
    public titleRef!: TemplateRef<unknown>;

    /**
     * the component should provide us with an already translated title.
     * this allow the expansion panel to not be aware of every translatiosn
     */
    public expansionPanelRef!: TemplateRef<unknown>;

    protected readonly secondaryColorLight = SECONDARY_COLOR_LIGHT;
    protected readonly secondaryColor = SECONDARY_COLOR;
    protected readonly transparentColor = TRANSPARENT_COLOR;

    constructor(
        @Inject(LOGGER) private logger: Logger,
        public layoutFacade: LayoutFacade,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        // force opening if collapsable becomes disabled.
        if (
            changes["collapsable"] &&
            !changes["collapsable"].currentValue &&
            changes["collapsable"].previousValue
        ) {
            this.collapsed = false;
        }
    }

    public ngAfterContentInit(): void {
        const partsReference =
            this.expansionPanelParts.toArray() as ExpansionPanelPartDirective[];

        const header = partsReference.find(
            (directive) => directive.hExpansionPanelPart === "header",
        );
        const content = partsReference.find(
            (directive) => directive.hExpansionPanelPart === "content",
        );
        const expansionPanel = partsReference.find(
            (directive) => directive.hExpansionPanelPart === "expansionPanel",
        );

        if (!header || !content) {
            this.logger.warn(
                [
                    "Bad usage of <h-expansion-panel> component,",
                    "did you set *hExpansionPanelPart='header'",
                    "AND *hExpansionPanelPart='content' ?",
                ].join(" "),
            );
            return;
        }
        this.titleRef = header.templateReference;
        this.contentRef = content.templateReference;
        if (expansionPanel) {
            this.expansionPanelRef = expansionPanel.templateReference;
        }
    }

    /**
     * Show or hide the expansion panel
     */
    public handleHeaderExpansionPanel(): void {
        if (!this.collapsable) {
            return;
        }

        this.collapsed = !this.collapsed;
        this.collapsedChange.emit({
            id: this.expansionPanelId,
            value: this.collapsed,
        });

        // expand action :
        if (!this.collapsed) {
            this.expand.emit(this.expansionPanelId);
            return;
        }

        this.collapse.emit(this.expansionPanelId);
    }

    /**
     * Called when expansion panel animation ends
     */
    public onAnimationEnd(event: AnimationEvent): void {
        // fromState must be different from void to indicate that the animation should only apply after the element enters the view.
        // toState must be equal to open to indicate that the animation should only apply when an expansion panel section is opened.
        if (
            event.fromState !== "void" &&
            event.toState === "open" &&
            this.adjustScroll
        ) {
            this.scrollExpansionPanelIntoView(event.element as HTMLElement);
        }
    }

    /**
     * Allow to scroll to a given selector
     */
    private scrollExpansionPanelIntoView(item: HTMLElement): void {
        if (item.parentElement) {
            scrollIntoView(item.parentElement, {
                block: this.scrollPosition,
                behavior: this.scrollBehavior,
            });
        }
    }
}
